import { useRouter } from "next/router"
import styles from "./index.module.scss"
import { useCommonConfirmDialog } from "src/components/hooks/useCommonConfirmDialog"
import { useLoading } from "src/components/hooks/useLoading"
import { useTieupToast } from "src/components/hooks/useTieupToast"
import GiftingAppeal from "./GiftingAppeal"
import GiftingForm, { FormParams } from "./GiftingForm"
import GiftingGuide from "./GiftingGuide"
import { postGift } from "src/models/gift/postGift"
import { fetchOwedGiftItems } from "src/models/gift/fetchOwedGiftItems"
import { ApiUserData } from "src/models/user/ApiUserData"
import { GIFT_TYPE_DEFINITIONS } from "src/models/gift/GIFT_TYPE_DEFINITIONS"
import useSWR from "swr"
import routes from "routes"

type Props = {
  user: ApiUserData
  isMe: boolean
}

const Gifting: React.FC<Props> = ({ user, isMe }) => {
  const router = useRouter()

  const { data: giftItems } = useSWR(
    "/v1/mypage/gift/owned",
    fetchOwedGiftItems,
    { revalidateOnMount: true, revalidateOnFocus: false },
  )

  const { fullScreenLoading } = useLoading()

  const { confirmDialog } = useCommonConfirmDialog()

  const { addToastError } = useTieupToast()

  const handleSubmit = (data: FormParams) => {
    if (isMe) return

    const giftType = GIFT_TYPE_DEFINITIONS.find(
      (v) => v.id === Number(data.gift_item_id),
    )

    if (giftType === undefined) return

    const postParams = {
      user: user,
      gift_item: {
        type: giftType,
        amount: data.amount,
      },

      send_message_id: data.send_message_id,
      send_free_message: data.send_free_message,
    }

    confirmDialog(
      `この内容で贈ります。よろしいですか？`,
      "はい",
      "いいえ",
      async () => {
        fullScreenLoading(true)

        await postGift(postParams)
          .then(() => {
            router.push(routes.userGiftCompleted(user.display_name))
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error)

            if (error.response.status === 429) {
              addToastError("送信可能なギフトの上限数を超えています。")
            } else {
              addToastError("処理に失敗しました")
            }
          })
        fullScreenLoading(false)
      },
    )
  }

  return (
    <div className={styles.gifting}>
      <GiftingGuide />

      <GiftingAppeal user={user} />

      {giftItems && (
        <GiftingForm
          giftItems={giftItems}
          onSubmit={handleSubmit}
          returnURL={routes.userGift(user.display_name)}
          isHiddenSubmitButton={isMe}
        />
      )}
    </div>
  )
}

export default Gifting
